/**
 * ArticleFootnotes
 * Hover/focus on a .footnote-ref shows a floating card containing the
 * corresponding footnote content. The native anchor jump still works
 * (for keyboard / no-JS / print).
 *
 * On touch devices, tap toggles the card.
 */
export default class ArticleFootnotes {
	constructor(bodySelector = '[data-article-body]', footnotesSelector = '.footnotes__content') {
		this.body = document.querySelector(bodySelector);
		this.footnotesEl = document.querySelector(footnotesSelector);
		if (!this.body || !this.footnotesEl) return;

		this.card = null;
		this.activeRef = null;
		this.hideTimer = null;
		this.isTouch = window.matchMedia('(hover: none)').matches;

		this.init();
	}

	init() {
		// Build lookup: footnote-id → HTML content
		this.footnotes = {};
		const items = this.footnotesEl.querySelectorAll('li[id], li');
		items.forEach((li, i) => {
			const id = li.id || `fn${i + 1}`;
			this.footnotes[id] = li.innerHTML;
		});

		// Find all in-body footnote refs
		const refs = this.body.querySelectorAll('a.footnote-ref, sup a, [data-footnote-ref]');
		refs.forEach((ref) => {
			ref.classList.add('footnote-ref');
			if (this.isTouch) {
				ref.addEventListener('click', (e) => {
					e.preventDefault();
					this.toggle(ref);
				});
			} else {
				ref.addEventListener('mouseenter', () => this.show(ref));
				ref.addEventListener('mouseleave', () => this.scheduleHide());
				ref.addEventListener('focus', () => this.show(ref));
				ref.addEventListener('blur', () => this.scheduleHide());
			}
		});

		// Build card once
		this.card = document.createElement('div');
		this.card.className = 'footnote-card';
		this.card.setAttribute('role', 'tooltip');
		this.card.innerHTML = '<span class="footnote-card__marker"></span><span class="footnote-card__body"></span>';
		this.card.addEventListener('mouseenter', () => this.cancelHide());
		this.card.addEventListener('mouseleave', () => this.scheduleHide());
		document.body.appendChild(this.card);
	}

	resolveTarget(ref) {
		// CKEditor footnote-ref usually points to #fn1 etc. via href
		const href = ref.getAttribute('href') || '';
		const id = href.replace(/^#/, '');
		if (this.footnotes[id]) return { id, html: this.footnotes[id] };
		// fallback: use textContent as 1-based index
		const num = parseInt((ref.textContent || '').trim(), 10);
		if (!isNaN(num)) {
			const keys = Object.keys(this.footnotes);
			const key = keys[num - 1];
			if (key) return { id: key, html: this.footnotes[key] };
		}
		return null;
	}

	show(ref) {
		this.cancelHide();
		const data = this.resolveTarget(ref);
		if (!data) return;

		const marker = ref.textContent.trim() || '¹';
		this.card.querySelector('.footnote-card__marker').textContent = marker;
		// strip the backref ↑ from li content
		let html = data.html.replace(/<a[^>]+href="#fnref[^"]*"[^>]*>.*?<\/a>/g, '');
		this.card.querySelector('.footnote-card__body').innerHTML = html;

		// position next to ref
		const refRect = ref.getBoundingClientRect();
		const cardWidth = 320;
		const margin = 12;
		let left = refRect.right + margin + window.scrollX;
		let top = refRect.top + window.scrollY - 8;

		if (left + cardWidth > window.scrollX + window.innerWidth - 16) {
			left = refRect.left + window.scrollX - cardWidth - margin;
		}
		if (left < 16) left = 16;

		this.card.style.left = `${left}px`;
		this.card.style.top = `${top}px`;
		this.card.classList.add('is-visible');
		this.activeRef = ref;
	}

	hide() {
		this.card.classList.remove('is-visible');
		this.activeRef = null;
	}

	toggle(ref) {
		if (this.activeRef === ref) this.hide();
		else this.show(ref);
	}

	scheduleHide() {
		this.cancelHide();
		this.hideTimer = setTimeout(() => this.hide(), 200);
	}

	cancelHide() {
		if (this.hideTimer) {
			clearTimeout(this.hideTimer);
			this.hideTimer = null;
		}
	}
}
